import DescriptionIcon from '@mui/icons-material/Description';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { Box, IconButton, Stack, styled, Tooltip, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';
import { useLogStore } from '../stores/logStore';
import { SelectStd } from './ui/SelectStd';

const LogContainer = styled(Box)(() => ({
  fontFamily: 'monospace',
  fontSize: '13px',
  backgroundColor: '#D8DCDF',
  color: '#ffffff',
  padding: '12px',
  borderRadius: '4px',
  overflowY: 'auto',
  overflowX: 'hidden',
  height: '100%',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  scrollbarWidth: 'thin',
  scrollbarColor: '#555 #2a2a2a',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#2a2a2a',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#555',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#777',
  },
}));

const LogLine = styled('div')(({ level }) => {
  const colors = {
    ERROR: '#ff5555',
    WARN: '#ffaa55',
    INFO: '#ffffff',
    DEBUG: '#888888',
  };
  return {
    color: colors[level] || '#ffffff',
    marginBottom: '2px',
  };
});

const ControlBar = styled(Stack)(() => ({
  padding: '8px 12px',
  backgroundColor: '#D8DCDF',
  borderRadius: '4px 4px 0 0',
  borderBottom: '1px solid #ddd',
}));

const LogViewer = ({ sx }) => {
  const initListener = useLogStore((s) => s.initListener);
  const loadInitialLogs = useLogStore((s) => s.loadInitialLogs);
  const logLines = useLogStore((s) => s.logLines);
  const levelFilter = useLogStore((s) => s.levelFilter);
  const setLevelFilter = useLogStore((s) => s.setLevelFilter);
  const sourceFilter = useLogStore((s) => s.sourceFilter);
  const setSourceFilter = useLogStore((s) => s.setSourceFilter);
  const openLogFile = useLogStore((s) => s.openLogFile);
  const openLogFolder = useLogStore((s) => s.openLogFolder);

  const logEndRef = useRef(null);
  const containerRef = useRef(null);
  const isAtBottom = useRef(true);

  // Initialize on mount (singleton - no cleanup needed)
  useEffect(() => {
    initListener();
    loadInitialLogs();
  }, [initListener, loadInitialLogs]);

  // Filter logs
  const logs = logLines.filter((log) => {
    const levelMatch = levelFilter === 'all' || log.level === levelFilter;
    const sourceMatch = sourceFilter === 'all' || log.source === sourceFilter;
    return levelMatch && sourceMatch;
  });

  // Track scroll position
  const handleScroll = () => {
    if (!containerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    isAtBottom.current = scrollHeight - scrollTop - clientHeight < 50;
  };

  // Auto-scroll only when at bottom
  useEffect(() => {
    if (isAtBottom.current && logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logLines.length]);

  const sourceOptions = [
    { value: 'all', label: 'ALL' },
    { value: 'APP', label: 'APP' },
    { value: 'BACKEND', label: 'BACKEND' },
  ];

  const levelOptions = [
    { value: 'all', label: 'ALL' },
    { value: 'ERROR', label: 'ERROR' },
    { value: 'WARN', label: 'WARN' },
    { value: 'INFO', label: 'INFO' },
    { value: 'DEBUG', label: 'DEBUG' },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, ...sx }}>
      <ControlBar direction="row" spacing={3} alignItems="center">
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2">Source:</Typography>
          <SelectStd
            value={sourceFilter}
            onChange={setSourceFilter}
            options={sourceOptions}
            width="110px"
          />
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2">Level:</Typography>
          <SelectStd
            value={levelFilter}
            onChange={setLevelFilter}
            options={levelOptions}
            width="100px"
          />
        </Stack>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Open log file">
          <IconButton size="small" onClick={openLogFile}>
            <DescriptionIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Open log folder">
          <IconButton size="small" onClick={openLogFolder}>
            <FolderOpenIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </ControlBar>

      <LogContainer ref={containerRef} onScroll={handleScroll}>
        {logs.map((log, idx) => (
          <LogLine key={`${log.timestamp}-${idx}`} level={log.level}>
            [{log.timestamp}][{log.level}][{log.source}] {log.message}
          </LogLine>
        ))}
        <div ref={logEndRef} />
      </LogContainer>
    </Box>
  );
};

export default LogViewer;
